import React, { useState } from "react";

import {
  ArrayUtils,
  formatDayDate,
  formatNumber,
  getAdminVerificationYearlyDataYear,
  getCropName,
  getEditingRequestButtonState,
  useGetCarbonProgramByFarmId,
  useGetFieldPractices,
  useInView,
  useTillageAndSeedingHelpers,
  useUpdateVerificationStatus,
} from "utils";

import { TILLAGE_DEPTH_RANGE_OPTIONS } from "consts";

import {
  CropFCPO,
  EDITING_REQUEST_TYPE,
  EditingRequestType,
  FieldVerification,
  PRACTICE_SLUG,
  PracticeFCPO,
  VerificationEditingRequest,
  VerificationStatus,
} from "models";

import { Button, Col, Icon, Message, Row, SkeletonWrapper, TagNormal, Typography } from "components";

import { CropDrawer } from "pages/admin/verifications/components/misc/CropDrawer";
import { EditingRequestModal, EditingRequestTarget, TargetableRequestType } from "pages/admin/verifications/components/misc/EditingRequestModal";
import { VerificationStatusDropdown } from "pages/admin/verifications/components/misc/VerificationStatusDropdown";

// Maps a tillage/seeding practice's slug to the (singular) editing-request type it targets
const TILLAGE_SEEDING_PRACTICE_REQUEST_TYPE: Record<string, TargetableRequestType> = {
  [PRACTICE_SLUG.TILLAGES]: EDITING_REQUEST_TYPE.TILLAGE,
  [PRACTICE_SLUG.SEEDINGS]: EDITING_REQUEST_TYPE.SEEDING,
};

interface TillageAndSeedingTableProps {
  field: FieldVerification;
  farmId: string;
  editingRequests: VerificationEditingRequest[];
}

export const TillageAndSeedingTable = ({ field, farmId, editingRequests }: TillageAndSeedingTableProps) => {
  const [cropToEdit, setCropToEdit] = useState<CropFCPO | null>(null);
  const [editingRequestTarget, setEditingRequestTarget] = useState<EditingRequestTarget>(null);

  const fieldId = field.id;
  const { getPracticeName, getDieselUsage, getCombinedPractices, sortPracticesByEndDate, getPracticeIconName } = useTillageAndSeedingHelpers();
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);
  const requirePreciseTillageDepth = carbonProgram?.fieldsManagement.requirePreciseTillageDepth === true;

  // Resolve a stored tillageDepth to its display text, depending on whether the carbon program requires a precise value or a range bucket
  const getTillageDepthDisplay = (tillageDepth: number | undefined | null): string => {
    if (tillageDepth === undefined || tillageDepth === null) return "-";
    if (requirePreciseTillageDepth) return `${formatNumber({ number: tillageDepth, decimalPlaces: 1 })} cm`;
    return TILLAGE_DEPTH_RANGE_OPTIONS.find((option) => option.value === tillageDepth)?.name ?? `${tillageDepth} cm`;
  };

  // Helper function to get editing requests for a specific tillage/seeding row
  const getPracticeEditingRequests = (practiceId: string, requestType: EditingRequestType): VerificationEditingRequest[] =>
    editingRequests.filter(
      (request) => request.requestType === requestType && (request.fieldTillageId === practiceId || request.fieldSeedingId === practiceId)
    );

  const { data: fieldPractices, isLoading } = useGetFieldPractices({ fieldId, inView, isAdmin: true });

  // Handler for clicking on a practice row to open the crop drawer
  const handlePracticeClick = (practice: PracticeFCPO) => {
    const associatedCrop = fieldPractices?.crops?.find((c) => c.id === practice.cropId);
    if (associatedCrop) {
      setCropToEdit(associatedCrop);
    }
  };

  const { mutate: updateStatus } = useUpdateVerificationStatus({ fieldId, farmId });

  // Handler for updating tillage/seeding verification status
  const handleStatusChange = (practiceId: string, practiceType: string, newStatus: VerificationStatus) => {
    // Allow null to reset verification status
    updateStatus(
      {
        practiceType,
        practiceId,
        verificationStatus: newStatus,
      },
      {
        onSuccess: () => {
          Message({ icon: "tick", content: "Verification status updated successfully" });
        },
      }
    );
  };

  // Get combined tillage and seeding practices
  const practices = getCombinedPractices(fieldPractices);

  // Requests whose target row no longer exists (e.g. it was deleted and replaced rather than edited in place)
  const currentTillageIds = new Set(
    practices
      .filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES)
      .map((practice) => practice.id)
      .filter((id): id is string => !!id)
  );
  const currentSeedingIds = new Set(
    practices
      .filter((practice) => (practice.type as string) === PRACTICE_SLUG.SEEDINGS)
      .map((practice) => practice.seedingPracticeId ?? practice.id)
      .filter((id): id is string => !!id)
  );
  const orphanedTillageSeedingEditingRequests = editingRequests.filter((request) => {
    if (request.reviewedAt !== null) return false;
    if (request.requestType === EDITING_REQUEST_TYPE.TILLAGE) return !currentTillageIds.has(request.fieldTillageId ?? "");
    if (request.requestType === EDITING_REQUEST_TYPE.SEEDING) return !currentSeedingIds.has(request.fieldSeedingId ?? "");
    return false;
  });
  const [showOrphanedEditingRequests, setShowOrphanedEditingRequests] = useState(false);

  // Group practices by year
  const practicesByYear = ArrayUtils.groupBy(practices, (practice) => practice?.year ?? "unknown");

  // Sort years in descending order
  const sortedYears = Object.keys(practicesByYear || {})
    .filter((year) => year !== "unknown")
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <div ref={ref} className="crop-table">
      <SkeletonWrapper isLoading={isLoading} type="block" height="20rem">
        {orphanedTillageSeedingEditingRequests.length > 0 &&
          (() => {
            const buttonState = getEditingRequestButtonState(orphanedTillageSeedingEditingRequests);
            return (
              <Row className="year-header" align="middle" justify="end">
                <Col>
                  <Button type={buttonState.type} className={`${buttonState.className} margin-right-250`} onClick={() => setShowOrphanedEditingRequests(true)}>
                    {`${buttonState.text} (${orphanedTillageSeedingEditingRequests.length})`}
                  </Button>
                </Col>
              </Row>
            );
          })()}
        {/* Table Header */}
        <Row className="crop-table-header">
          <Col md={5} className="header-cell">
            practice name
          </Col>
          <Col md={3} className="header-cell">
            diesel usage
          </Col>
          <Col md={2} className="header-cell">
            tillage depth
          </Col>
          <Col md={3} className="header-cell">
            date range
          </Col>
          <Col md={2} className="header-cell">
            selection type
          </Col>
          <Col md={4} className="header-cell">
            status
          </Col>
          <Col md={2} className="header-cell">
            editing requests
          </Col>
          <Col md={3} className="header-cell">
            actions
          </Col>
        </Row>

        {/* Table Body grouped by year */}
        {sortedYears.map((year) => {
          const yearPractices = practicesByYear[year];
          const sortedPractices = sortPracticesByEndDate([...yearPractices]);

          return (
            <div key={year} className="year-group">
              {/* Year header */}
              <Row className="year-header" align="middle">
                <Col md={5}>
                  <Typography variant="h4" className="year-title">
                    {year}
                  </Typography>
                </Col>
                <Col md={3}></Col>
                <Col md={2}></Col>
                <Col md={3}></Col>
                <Col md={2}></Col>
                <Col md={4}></Col>
                <Col md={2}></Col>
                <Col md={3}></Col>
              </Row>
              {/* Practices for this year */}
              {sortedPractices.map((practice) => {
                const cropName = getCropName(practice.cropId, fieldPractices);
                const dieselUsage = getDieselUsage(practice);
                return (
                  <Row key={practice.id} className="crop-row pointer" onClick={() => handlePracticeClick(practice)}>
                    <Col md={5} className={`crop-cell ${practice.soilPlus3DataCollection ? "highlight-soil-plus3-collection" : ""}`}>
                      <div className="crop-name-cell">
                        <div className={`crop-indicator ${practice.verificationStatus}`} />
                        <Icon iconName={getPracticeIconName(practice.type)} fill="none" className="icon inline margin-right-50" size="s" />
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
                          <Typography variant="paragraph-s" bold>
                            {getPracticeName(practice.type, practice.assignedPracticeId)}
                          </Typography>
                          {cropName && (
                            <Typography variant="paragraph-xs" color="grey-600">
                              {cropName}
                            </Typography>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={3} className="crop-cell">
                      <Typography variant="paragraph-xs">
                        {dieselUsage !== null && dieselUsage !== undefined ? `${formatNumber({ number: dieselUsage, decimalPlaces: 2 })} L/ha` : "-"}
                      </Typography>
                    </Col>
                    <Col md={2} className={`crop-cell ${practice.tillageDepthSoilPlus3DataCollection ? "highlight-soil-plus3-collection" : ""}`}>
                      <Typography variant="paragraph-xs">
                        {practice.type === PRACTICE_SLUG.TILLAGES ? getTillageDepthDisplay(practice.tillageDepth) : "-"}
                      </Typography>
                    </Col>
                    <Col md={3} className={`crop-cell ${practice.applicationDatesSoilPlus3DataCollection ? "highlight-soil-plus3-collection" : ""}`}>
                      <Typography variant="paragraph-xs">
                        {(practice.startDate ?? practice.fallbackDates.startDate) && (practice.endDate ?? practice.fallbackDates.endDate)
                          ? `${formatDayDate((practice.startDate ?? practice.fallbackDates.startDate)!)} - ${formatDayDate(
                              (practice.endDate ?? practice.fallbackDates.endDate)!
                            )}`
                          : practice.endDate ?? practice.fallbackDates.endDate
                          ? formatDayDate((practice.endDate ?? practice.fallbackDates.endDate)!)
                          : "-"}
                      </Typography>
                    </Col>
                    <Col md={2} className="crop-cell">
                      {practice.selectionType ? <TagNormal text={practice.selectionType} color="blue" /> : "-"}
                    </Col>
                    <Col md={4} className="crop-cell">
                      <VerificationStatusDropdown
                        status={practice.verificationStatus ?? null}
                        cropId={practice.id!}
                        onStatusChange={(_, status) => {
                          // For seedings, use seedingPracticeId; for tillages, use practice.id
                          handleStatusChange(practice.seedingPracticeId ?? practice.id!, practice.type, status);
                        }}
                      />
                    </Col>
                    <Col md={2} className="crop-cell">
                      {(() => {
                        const requestType = TILLAGE_SEEDING_PRACTICE_REQUEST_TYPE[practice.type];
                        // For seedings, target the seeding practice id; for tillages, target the practice id itself
                        const practiceId = practice.seedingPracticeId ?? practice.id!;
                        const practiceEditingRequests = getPracticeEditingRequests(practiceId, requestType);
                        const completedCount = practiceEditingRequests.filter((req) => req.completedAt).length;
                        return <Typography variant="paragraph-xs">{`${completedCount}/${practiceEditingRequests.length}`}</Typography>;
                      })()}
                    </Col>
                    <Col md={3} className="crop-cell">
                      {(() => {
                        const requestType = TILLAGE_SEEDING_PRACTICE_REQUEST_TYPE[practice.type];
                        const practiceId = practice.seedingPracticeId ?? practice.id!;
                        const practiceEditingRequests = getPracticeEditingRequests(practiceId, requestType);
                        const buttonState = getEditingRequestButtonState(practiceEditingRequests);

                        return (
                          <Button
                            type={buttonState.type}
                            className={buttonState.className}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRequestTarget({
                                requestType,
                                id: practiceId,
                                label: getPracticeName(practice.type, practice.assignedPracticeId) ?? "this practice",
                              });
                            }}
                          >
                            {buttonState.text}
                          </Button>
                        );
                      })()}
                    </Col>
                  </Row>
                );
              })}
            </div>
          );
        })}

        {/* Empty state */}
        {sortedYears.length === 0 && !isLoading && (
          <Row className="empty-state">
            <Col md={24}>
              <Typography variant="paragraph-s" className="text-disabled">
                No tillage or seeding practices found for this field
              </Typography>
            </Col>
          </Row>
        )}
      </SkeletonWrapper>
      <CropDrawer
        crop={cropToEdit}
        showDrawer={!!cropToEdit}
        onClose={() => setCropToEdit(null)}
        fieldPractices={fieldPractices}
        field={field}
        farmId={farmId}
      />
      <EditingRequestModal
        target={editingRequestTarget}
        fieldId={fieldId}
        showModal={!!editingRequestTarget}
        onClose={() => setEditingRequestTarget(null)}
        farmId={farmId}
        editingRequests={editingRequests}
        verificationYear={getAdminVerificationYearlyDataYear()}
      />
      <EditingRequestModal
        reviewOnlyEditingRequests={orphanedTillageSeedingEditingRequests}
        fieldId={fieldId}
        showModal={showOrphanedEditingRequests}
        onClose={() => setShowOrphanedEditingRequests(false)}
        farmId={farmId}
        editingRequests={editingRequests}
        verificationYear={getAdminVerificationYearlyDataYear()}
      />
    </div>
  );
};
